import { useState } from 'react';
import { T } from './theme';
import ProgressRing from './ProgressRing';
import { TAG_ACTIVATION_URL } from '../../config/links';

const StatLine = ({ stats, locked }) => {
  const parts = [`${stats.modules} modul`];
  if (!locked) {
    parts.push(`${stats.lessons} lecke`);
    if (stats.quizzes > 0) parts.push(`${stats.quizzes} kvíz`);
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 0', color: T.faint, fontSize: 13 }}>
      {parts.map((p, i) => (
        <span key={p} style={{ whiteSpace: 'nowrap' }}>
          {i > 0 && <span style={{ margin: '0 10px', opacity: 0.6 }}>·</span>}
          {p}
        </span>
      ))}
    </div>
  );
};

const ModuleList = ({ modules, locked }) => {
  const [open, setOpen] = useState(false);
  const completed = modules.filter(m => m.complete).length;

  return (
    <div style={{ borderTop: `1px solid ${T.line}`, paddingTop: 16 }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          color: T.muted, fontSize: 13, fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 8,
        }}
      >
        <span style={{ display: 'inline-block', transition: 'transform 0.2s', transform: open ? 'rotate(90deg)' : 'none' }}>›</span>
        Modulok ({modules.length})
        {!locked && completed > 0 && (
          <span style={{ color: T.faint, fontWeight: 400 }}>· {completed} kész</span>
        )}
      </button>

      {open && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
          {modules.map(m => (
            <span
              key={m.id}
              title={locked ? m.title : `${m.title} — ${m.done}/${m.total} lecke`}
              style={{
                fontSize: 12, lineHeight: 1.3, padding: '6px 11px', borderRadius: 999,
                maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                color: m.complete ? T.accent : T.muted,
                background: m.complete ? T.accentSoft : T.surface,
                border: `1px solid ${m.complete ? T.accentLine : T.line}`,
              }}
            >
              {m.complete && '✓ '}{m.title}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const PrimaryButton = ({ children, onClick, href }) => {
  const style = {
    display: 'inline-flex', alignItems: 'center', gap: 10, maxWidth: '100%',
    background: T.accent, color: '#07070b',
    border: 'none', borderRadius: 12, padding: '13px 22px',
    fontWeight: 700, fontSize: 15, cursor: 'pointer', textDecoration: 'none',
    boxShadow: '0 8px 24px -10px rgba(0,210,200,0.55)',
    transition: 'transform 0.15s, box-shadow 0.15s',
  };
  const hover = {
    onMouseEnter: e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 12px 28px -10px rgba(0,210,200,0.7)'; },
    onMouseLeave: e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = style.boxShadow; },
  };
  const label = <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{children}</span>;
  return href
    ? <a href={href} target="_blank" rel="noopener noreferrer" style={style} {...hover}>{label}<span aria-hidden="true">→</span></a>
    : <button type="button" onClick={onClick} style={style} {...hover}>{label}<span aria-hidden="true">→</span></button>;
};

const frame = (locked) => ({
  position: 'relative',
  borderRadius: T.radius,
  padding: 1, // gradient hairline border
  background: locked
    ? `linear-gradient(160deg, rgba(255,255,255,0.14), ${T.line} 45%, ${T.line})`
    : `linear-gradient(160deg, rgba(0,210,200,0.55), rgba(0,210,200,0.12) 40%, ${T.line})`,
  boxShadow: locked ? '0 24px 60px -36px rgba(0,0,0,0.8)' : '0 30px 80px -40px rgba(0,210,200,0.35)',
});

const inner = (isMobile) => ({
  borderRadius: T.radius - 1,
  background: 'linear-gradient(180deg, #131826 0%, #0f131c 100%)',
  padding: isMobile ? '24px 20px' : '34px 36px',
  display: 'flex', flexDirection: 'column', gap: 22,
});

const STEPS = [
  'Regisztrálsz az egyik partner tőzsdén a web3kurzus linkjén.',
  'Minimális befizetést teszel a tőzsdei számládra.',
  'A web3kurzus.hu dashboardon elküldöd a tőzsdei azonosítódat (UID). Ellenőrzés után megkapod a TAG hozzáférést.',
];

const FeaturedCourse = ({ course, outline, locked, onStart, onOverview, isMobile }) => {
  const { stats, pct, done, hasProgress, allDone, nextLesson } = outline;

  return (
    <section style={frame(locked)}>
      <div style={inner(isMobile)}>
        <div style={{ display: 'flex', gap: 28, alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', color: locked ? T.faint : T.accent, marginBottom: 10 }}>
              {locked ? 'TAG-TAGOKNAK' : hasProgress ? 'FOLYAMATBAN' : 'A TANFOLYAMOD'}
            </div>
            <h2 style={{
              margin: 0, color: T.text, fontSize: isMobile ? 24 : 30, fontWeight: 800,
              letterSpacing: '-0.015em', lineHeight: 1.15,
            }}>
              {course.title}
            </h2>
            {course.description && (
              <p style={{ margin: '10px 0 14px', color: T.muted, fontSize: 15, lineHeight: 1.6, maxWidth: 560 }}>
                {course.description}
              </p>
            )}
            <StatLine stats={stats} locked={locked} />
          </div>

          {!locked && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
              <ProgressRing pct={pct} size={isMobile ? 64 : 76} />
              {isMobile && (
                <div style={{ color: T.muted, fontSize: 13, lineHeight: 1.45 }}>
                  {done > 0 ? `${done} / ${stats.lessons} lecke kész` : 'Még nem kezdted el'}
                </div>
              )}
            </div>
          )}
        </div>

        {locked ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {STEPS.map((s, i) => (
                <li key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', color: T.body, fontSize: 14, lineHeight: 1.55 }}>
                  <span style={{
                    width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 700, color: T.accent, background: T.accentSoft, border: `1px solid ${T.accentLine}`,
                  }}>{i + 1}</span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
            <div><PrimaryButton href={TAG_ACTIVATION_URL}>TAG aktiválása</PrimaryButton></div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {!hasProgress && nextLesson && (
              <div style={{ color: T.muted, fontSize: 14 }}>
                Kezdd az első leckével: <span style={{ color: T.text, fontWeight: 600 }}>{nextLesson.title}</span>
              </div>
            )}
            {hasProgress && !isMobile && (
              <div style={{ color: T.muted, fontSize: 14 }}>
                {allDone ? 'Minden leckét teljesítettél.' : `${done} / ${stats.lessons} lecke kész`}
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 18 }}>
              {nextLesson && (
                <PrimaryButton onClick={() => onStart(nextLesson.id)}>
                  {allDone ? 'Ismétlés az elejéről' : hasProgress ? `Folytatás: ${nextLesson.title}` : 'Kezdés'}
                </PrimaryButton>
              )}
              <button
                type="button"
                onClick={() => onOverview(course.slug)}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: T.muted, fontSize: 14, fontWeight: 600 }}
                onMouseEnter={e => { e.currentTarget.style.color = T.text; }}
                onMouseLeave={e => { e.currentTarget.style.color = T.muted; }}
              >
                Tanfolyam áttekintése
              </button>
            </div>
          </div>
        )}

        {outline.modules.length > 0 && <ModuleList modules={outline.modules} locked={locked} />}
      </div>
    </section>
  );
};

// Further accessible courses, one compact row each.
export const CourseRow = ({ course, outline, onStart, isMobile }) => (
  <div
    style={{
      display: 'flex', alignItems: 'center', gap: 16, padding: isMobile ? '16px' : '18px 22px',
      background: T.surface, border: `1px solid ${T.line}`, borderRadius: 16,
      transition: 'border-color 0.15s, background 0.15s', cursor: outline.nextLesson ? 'pointer' : 'default',
    }}
    onClick={() => outline.nextLesson && onStart(outline.nextLesson.id)}
    onMouseEnter={e => { e.currentTarget.style.borderColor = T.accentLine; e.currentTarget.style.background = T.surfaceHover; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = T.line; e.currentTarget.style.background = T.surface; }}
  >
    <ProgressRing pct={outline.pct} size={46} stroke={4} />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ color: T.text, fontWeight: 700, fontSize: 16, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {course.title}
      </div>
      <div style={{ color: T.faint, fontSize: 13, marginTop: 3 }}>
        {outline.stats.modules} modul · {outline.stats.lessons} lecke
      </div>
    </div>
    <span style={{ color: T.accent, fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {outline.hasProgress ? 'Folytatás' : 'Kezdés'} →
    </span>
  </div>
);

export default FeaturedCourse;
